import type { ModifierOptionRow } from "@/modules/catalog/domain/types";
import { FormRoot, FormInput, FormPrice, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Modal } from "@/shared/ui/Modal";


import { useEffect } from "react";
import { z } from "zod";

const OptionFormSchema = z.object({
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ຕົວເລືອກ"),
  priceDeltaLak: z.number().int(),
  costDeltaLak: z.number().int().nullable().optional(),
});

type OptionFormValues = z.infer<typeof OptionFormSchema>;

export type ModifierOptionPayload = {
  name: string;
  priceDeltaLak: number;
  costDeltaLak: number | null;
};

type ModifierOptionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  option: ModifierOptionRow | null;
  onSubmit: (payload: ModifierOptionPayload) => Promise<void>;
  submitting: boolean;
};

export function ModifierOptionModal({
  open,
  onOpenChange,
  option,
  onSubmit,
  submitting,
}: ModifierOptionModalProps) {
  const isEdit = !!option;

  const methods = RHF.useForm<OptionFormValues>({
    resolver: zodResolver(OptionFormSchema),
    defaultValues: {
      name: option?.name ?? "",
      priceDeltaLak: option?.priceDeltaLak ?? 0,
      costDeltaLak: option?.costDeltaLak ?? null,
    },
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        name: option?.name ?? "",
        priceDeltaLak: option?.priceDeltaLak ?? 0,
        costDeltaLak: option?.costDeltaLak ?? null,
      });
    }
  }, [
    open,
    option?.id,
    option?.name,
    option?.priceDeltaLak,
    option?.costDeltaLak,
    methods.reset,
  ]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "ແກ້ໄຂຕົວເລືອກ" : "ເພີ່ມຕົວເລືອກ"}
      description={isEdit ? "ແກ້ໄຂຂໍ້ມູນຕົວເລືອກ" : "ຕື່ມຂໍ້ມູນຕົວເລືອກໃໝ່"}
      size="sm"
    >
      <FormRoot<OptionFormValues>
        methods={methods}
        onSubmit={async (values) => {
          await onSubmit({
            name: values.name,
            priceDeltaLak: values.priceDeltaLak,
            costDeltaLak: values.costDeltaLak ?? null,
          });
          onOpenChange(false);
        }}
        className="space-y-4"
      >
        <FormInput
          name="name"
          label="ຊື່ຕົວເລືອກ"
          placeholder="ຕົວຢ່າງ: Extra Oat Milk"
          requiredMark
        />
        <FormPrice
          name="priceDeltaLak"
          label="ລາຄາເພີ່ມ (LAK)"
          placeholder="ຕົວຢ່າງ: 5000"
          requiredMark
          currencySymbol="₭"
        />

        <FormPrice
          name="costDeltaLak"
          label="ຕົ້ນທຶນເພີ່ມ (LAK) (ສຳເລັດຮູບ)"
          placeholder="ຕົວຢ່າງ: 5000"
          currencySymbol="₭"
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button type="submit" isLoading={submitting}>
            {isEdit ? "ບັນທຶກ" : "ເພີ່ມ"}
          </Button>
        </div>
      </FormRoot>
    </Modal>
  );
}
